import React, { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    CardHeader,
    Form,
    Input,
    Button,
    FormFeedback,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Loader from "../../../Components/Common/Loader";
import api, { handleApiError } from "../../../helpers/api";
import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import logoDark from "../../../assets/images/Confidoo.png";
import logo from "../../../assets/images/lgo.png";
import { useTranslation } from "react-i18next";

// EcommerceOrderProduct component (unchanged)
const EcommerceOrderProduct = ({ item, index, handleQuantityChange, handleRemoveProduct }) => {
    const { t } = useTranslation();


    const product = item.product;
    if (!product) {
        return <tr><td colSpan="5">{t("ecommerce_order_detail.product_not_available")}</td></tr>;
    }
    return (
        <tr>
            <td>
                <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 me-3">
                        {product.images[0] && (
                            <img
                                src={product.images[0].url}
                                alt={product.name || t("ecommerce_order_detail.unknown_product")}
                                className="avatar-md bg-light rounded"
                                style={{ width: "60px", height: "60px" }}
                            />
                        )}
                    </div>
                    <div className="flex-grow-1">
                        <h5 className="fs-16 mb-1">
                            <Link
                                to={`/apps-ecommerce-product-details/${product._id}`}
                                className="text-dark"
                            >
                                {product.name || t("ecommerce_order_detail.unknown_product")}
                            </Link>
                        </h5>
                        <p className="text-muted mb-0 fs-14">{t("ecommerce_order_detail.category")}: {product.category?.name || "N/A"}</p>
                    </div>
                </div>
            </td>
            <td>{product.price} DT</td>
            <td>
                <div className="d-flex align-items-center">
                    <Button
                        color="light"
                        size="sm"
                        onClick={() => handleQuantityChange(index, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                    >
                        -
                    </Button>
                    <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                        className="form-control form-control-sm mx-2"
                        style={{ width: "60px", textAlign: "center" }}
                    />
                    <Button
                        color="light"
                        size="sm"
                        onClick={() => handleQuantityChange(index, item.quantity + 1)}
                    >
                        +
                    </Button>
                </div>
            </td>
            <td>
                <Button
                    color="danger"
                    size="sm"
                    onClick={() => handleRemoveProduct(index)}
                >
                    {t("ecommerce_order_detail.actions.remove")}
                </Button>
            </td>
            <td className="text-end">{(product.price * item.quantity).toFixed(2)} DT</td>
        </tr>
    );
};

const EcommerceOrderDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [error, setError] = useState(null);
    const [isEditingAddress, setIsEditingAddress] = useState(false);
    const [removeModal, setRemoveModal] = useState(false);
    const [removeIndex, setRemoveIndex] = useState(null);
    const [tva, setTva] = useState(null);
    const [tvaError, setTvaError] = useState(null);
    const [isLastProduct, setIsLastProduct] = useState(false);
    const [productName, setProductName] = useState("");
    const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                const response = await api.get(`/orders/${id}`);
                setOrder(response.data.data);
                setError(null);
            } catch (error) {
                handleApiError(error, setError);
                toast.error(error.response?.data?.message || t("ecommerce_order_detail.toasts.fetch_error"));
            }
        };
        const fetchTva = async () => {
            try {
                const response = await api.get("/products/tva");
                setTva(response.data.tva);
                setTvaError(null);
            } catch (error) {
                handleApiError(error, setTvaError, navigate);
                toast.error(error.response?.data?.message || t("ecommerce_orders.toasts.tva_fetch_error"));
            }
        };
        fetchOrder();
        fetchTva();
    }, [id, t]);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            shippingAddress: order?.shippingAddress || {
                _id: "",
                fullName: "",
                street: "",
                city: "",
                region: "",
                postalCode: "",
                phoneNumber: "",
            },
            items: order?.items || [],
        },
        validationSchema: Yup.object({
            shippingAddress: Yup.object({
                fullName: Yup.string().required(t("ecommerce_order_detail.form.full_name_required")),
                street: Yup.string().required(t("ecommerce_order_detail.form.street_required")),
                city: Yup.string().required(t("ecommerce_order_detail.form.city_required")),
                region: Yup.string().required(t("ecommerce_order_detail.form.region_required")),
                postalCode: Yup.string().required(t("ecommerce_order_detail.form.postal_code_required")),
                phoneNumber: Yup.string().required(t("ecommerce_order_detail.form.phone_number_required")),
            }),
            items: Yup.array()
                .of(
                    Yup.object({
                        product: Yup.object({
                            _id: Yup.string().required(t("ecommerce_order_detail.form.product_id_required")),
                        }).required(t("ecommerce_order_detail.form.product_required")),
                        quantity: Yup.number()
                            .min(1, t("ecommerce_order_detail.form.quantity_min"))
                            .required(t("ecommerce_order_detail.form.quantity_required")),
                        price: Yup.number()
                            .min(0, t("ecommerce_order_detail.form.price_non_negative"))
                            .required(t("ecommerce_order_detail.form.price_required")),
                    })
                )
                .min(1, t("ecommerce_order_detail.form.items_required")),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const updateData = {};
                if (
                    JSON.stringify(values.shippingAddress) !==
                    JSON.stringify(order.shippingAddress)
                ) {
                    await api.patch(`/address/${values.shippingAddress._id}`, values.shippingAddress);
                }
                const newItems = values.items.map((item) => ({
                    product: item.product._id,
                    quantity: parseInt(item.quantity),
                    price: item.price,
                    customSpecs: item.customSpecs || {},
                }));
                const oldItems = order.items.map((item) => ({
                    product: item.product._id,
                    quantity: parseInt(item.quantity),
                    price: item.price,
                    customSpecs: item.customSpecs || {},
                }));
                if (JSON.stringify(newItems) !== JSON.stringify(oldItems)) {
                    updateData.items = newItems;
                    updateData.totalPrice = newItems.reduce(
                        (sum, item) => sum + item.price * item.quantity,
                        0
                    );
                }
                if (Object.keys(updateData).length > 0) {
                    const response = await api.patch(`/orders/${id}`, updateData);
                    setOrder(response.data.data);
                    toast.success(t("ecommerce_order_detail.toasts.update_success"));
                }
                setIsEditingAddress(false);
            } catch (error) {
                handleApiError(error, setError);
                toast.error(error.response?.data?.message || t("ecommerce_order_detail.toasts.update_error"));
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleQuantityChange = (index, value) => {
        const updatedItems = [...validation.values.items];
        updatedItems[index].quantity = Math.max(1, parseInt(value) || 1);
        validation.setFieldValue("items", updatedItems);
        validation.submitForm();
    };

    const handleRemoveProduct = (index) => {
        const isLast = validation.values.items.length === 1;
        const name = validation.values.items[index].product.name || t("ecommerce_order_detail.unknown_product");
        setRemoveIndex(index);
        setIsLastProduct(isLast);
        setProductName(name);
        setRemoveModal(true);
    };

    const confirmRemove = () => {
        if (isLastProduct) {
            api.delete(`/orders/${id}`)
                .then(() => {
                    toast.success(t("ecommerce_order_detail.toasts.delete_order_success"));
                    navigate("/apps-ecommerce-orders");
                })
                .catch((error) => {
                    handleApiError(error, setError);
                    toast.error(error.response?.data?.message || t("ecommerce_order_detail.toasts.delete_order_error"));
                });
        } else {
            const updatedItems = [...validation.values.items];
            updatedItems.splice(removeIndex, 1);
            validation.setFieldValue("items", updatedItems);
            validation.submitForm();
        }
        setRemoveModal(false);
    };

    const generateInvoicePDF = async (order) => {
        if (!order?.items || !order?.user || !order?.shippingAddress) {
            throw new Error(t("ecommerce_order_detail.toasts.invalid_order_data"));
        }

        const doc = new jsPDF({
            orientation: "portrait",
            unit: "pt",
            format: "a4",
        });

        const margin = 50;
        let currentY = 330; // starting Y for products
        const pageWidth = doc.internal.pageSize.getWidth();

        // Calculate totals
        const subtotal = order.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        const taxRate = tva ? tva / 100 : 0.19; // default to 19% if tva not available
        const tax = subtotal * taxRate;
        const deliveryFee = subtotal > 300 ? 0 : 7;
        const totalPrice = subtotal + tax + deliveryFee;

        // --- Company Header ---
        doc.setFont("helvetica", "bold").setFontSize(28);
        try {
            doc.addImage(logo, "PNG", (pageWidth / 2) - 65, 12, 130, 39);
        } catch (error) {
            console.error("Failed to load logoDark:", error);
            try {
                doc.addImage(logoDark, "PNG", (pageWidth / 2) - 65, 12, 130, 39);

            } catch (fallbackError) {
                console.error("Failed to load fallback logo:", fallbackError);
            }
        }

        doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(0, 0, 0);
        const companyInfo = [
            "Bureau 305, 3ème étage Africa Mall",
            "Avenue Mustapha Hjeij, Ariana 1002, Tunisie",
            "Email: Contact@confidoo.com.tn",
            "Téléphone: +21694463209",
            "Site: https://store.confidoo.com.tn"
        ];
        companyInfo.forEach((line, i) => doc.text(line, margin, 85 + i * 15));

        // --- Invoice Info (Right) ---
        const rightX = pageWidth - margin;
        doc.setTextColor("#2563eb").setFont("helvetica", "bold").setFontSize(20);
        doc.text("FACTURE", rightX, 85, { align: "right" });

        doc.setTextColor(0, 0, 0).setFont("helvetica", "normal").setFontSize(12);
        doc.text(`Numéro: ${order.reference || "N/A"}`, rightX, 110, { align: "right" });
        doc.text(`Date: ${new Date(order.createdAt).toLocaleDateString("fr-FR")}`, rightX, 125, { align: "right" });
        doc.text(`Montant: ${totalPrice.toFixed(2)} DT`, rightX, 140, { align: "right" });

        // Horizontal line
        doc.setDrawColor(204, 204, 204).line(margin, 180, pageWidth - margin, 180);

        // --- Customer Info ---
        doc.setTextColor("#2563eb").setFont("helvetica", "bold").setFontSize(16);
        doc.text("INFORMATIONS CLIENT", margin, 200);

        doc.setTextColor(0, 0, 0).setFont("helvetica", "bold").setFontSize(12);
        doc.text(`${order.user.firstname || ""} ${order.user.lastname || ""}`, margin, 225);

        doc.setFont("helvetica", "normal").setFontSize(10);
        doc.text(order.shippingAddress.street || "N/A", margin, 245);
        doc.text(`${order.shippingAddress.city || ""}, ${order.shippingAddress.region || ""}`, margin, 260);
        if (order.shippingAddress.postalCode) doc.text(`Code postal: ${order.shippingAddress.postalCode}`, margin, 275);
        doc.text(`Téléphone: ${order.shippingAddress.phoneNumber || order.user.phoneNumber || "N/A"}`, margin, 290);

        // Payment Info (right side)
        doc.setTextColor("#2563eb").setFont("helvetica", "bold").setFontSize(16);
        doc.text("PAIEMENT", pageWidth - 200, 200);

        doc.setTextColor(0, 0, 0).setFont("helvetica", "normal").setFontSize(12);
        doc.text("Méthode: Paiement à la livraison", pageWidth - 200, 225);

        // --- Products Table ---
        doc.setTextColor("#2563eb").setFont("helvetica", "bold").setFontSize(16);
        doc.text("DÉTAILS DE LA COMMANDE", margin, currentY);

// Table header
        // Table header
        currentY += 30;
        const tableWidth = 495;
        const rowHeight = 60;
        doc.setFillColor("#f8f9fa").rect(margin, currentY, tableWidth, 25, "F");
        doc.setDrawColor("#dee2e6").rect(margin, currentY, tableWidth, 25, "S");

        const headers = ["#", "QR", "Produit", "Référence", "Prix Unit.", "Qté", "Total"];
        const headerX = [55, 80, 135, 290, 410, 470, 530];

// Align header vertically center
        const headerY = currentY + 18; // roughly middle of header row (25 height)
        doc.setFont("helvetica", "bold").setFontSize(10).setTextColor(0, 0, 0);
        headers.forEach((text, i) => doc.text(text, headerX[i], headerY, { align: "left" }));

        currentY += 25;

// Table rows
        const qrCodes = await Promise.all(order.items.map(item =>
            QRCode.toDataURL(`https://store.confidoo.com.tn/product-details/${item.product.slug || "unknown"}/${item.product._id}`, { width: 200, margin: 1 })
                .catch(() => null)
        ));

        order.items.forEach((item, i) => {
            const qrCode = qrCodes[i];

            // Row background
            doc.setFillColor(i % 2 === 0 ? "#ffffff" : "#f8f9fa").rect(margin, currentY, tableWidth, rowHeight, "F");
            doc.setDrawColor("#dee2e6").rect(margin, currentY, tableWidth, rowHeight, "S");

            // Vertical center for row content
            const rowMiddleY = currentY + rowHeight / 2 + 4; // 4px offset for visual center

            // Index
            doc.text(`${i + 1}`, 55, rowMiddleY);

            // QR
            if (qrCode) doc.addImage(qrCode, "PNG", 80, currentY + 5, 50, 50);

            // Product Name
            doc.setFont("helvetica", "bold").setFontSize(10);
            doc.text(item.product.name || "Unknown Product", 135, rowMiddleY - 10, { maxWidth: 150, lineBreak: true });

            // Reference
            doc.setFont("helvetica", "normal").setFontSize(9);
            doc.text(item.product.reference || "N/A", 290, rowMiddleY);

            // Unit Price
            doc.text(`${item.price.toFixed(2)} DT`, 410, rowMiddleY, { maxWidth: 70, align: "right" });

            // Quantity
            doc.setFont("helvetica", "bold");
            doc.text(item.quantity.toString(), 470, rowMiddleY, { maxWidth: 20, align: "center" });

            // Total
            doc.text(`${(item.price * item.quantity).toFixed(2)} DT`, 530, rowMiddleY, { maxWidth: 60, align: "right" });

            currentY += rowHeight;
        });



        // --- Totals ---
        currentY += 30;
        const totalsX = 360, totalsValueX = 540;

        doc.setFont("helvetica", "normal").setFontSize(12);
        doc.text("Sous-total HT:", totalsX, currentY);
        doc.text(`${subtotal.toFixed(2)} DT`, totalsValueX, currentY, { align: "right" });

        currentY += 20;
        doc.text(`TVA (${(taxRate * 100).toFixed(0)}%):`, totalsX, currentY);
        doc.text(`${tax.toFixed(2)} DT`, totalsValueX, currentY, { align: "right" });

        currentY += 20;
        doc.text("Frais de livraison:", totalsX, currentY);
        doc.text(`${deliveryFee.toFixed(2)} DT`, totalsValueX, currentY, { align: "right" });

        currentY += 15;
        doc.setDrawColor(0, 0, 0).line(totalsX, currentY, pageWidth - margin, currentY);

        currentY += 20;
        doc.setFont("helvetica", "bold").setFontSize(14).setTextColor("#2563eb");
        doc.text("TOTAL TTC:", totalsX, currentY);
        doc.text(`${totalPrice.toFixed(2)} DT`, totalsValueX, currentY, { align: "right" });

        // --- Terms ---
        currentY += 60;
        doc.setFont("helvetica", "bold").setFontSize(14).setTextColor("#2563eb");
        doc.text("CONDITIONS DE PAIEMENT", margin, currentY);

        currentY += 20;
        doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(0, 0, 0);
        doc.text(
            "Le paiement doit être effectué à la livraison. En cas de non-paiement à la réception, " +
            "les frais mentionnés ci-dessus seront facturés selon les conditions générales de vente. " +
            "Cette facture est générée automatiquement et ne nécessite pas de signature.",
            margin,
            currentY,
            { maxWidth: pageWidth - 2 * margin, lineBreak: true }
        );

        // --- Footer ---
        currentY += 80;
        doc.setFont("helvetica", "bold").setFontSize(12).setTextColor("#2563eb");
        doc.text("Merci de votre confiance • CONFIDOO", pageWidth / 2, currentY, { align: "center" });

        return doc;
    };

    const printInvoice = async () => {
        if (!order) {
            toast.error(t("ecommerce_order_detail.toasts.no_order_data"));
            return;
        }
        setIsGeneratingInvoice(true);
        try {
            const doc = await generateInvoicePDF(order);
            doc.output("dataurlnewwindow");
        } catch (error) {
            console.error("Error generating invoice for printing:", error);
            toast.error(error.message || t("ecommerce_order_detail.toasts.invoice_generation_error"));
        } finally {
            setIsGeneratingInvoice(false);
        }
    };

    const downloadInvoice = async () => {
        if (!order) {
            toast.error(t("ecommerce_order_detail.toasts.no_order_data"));
            return;
        }
        setIsGeneratingInvoice(true);
        try {
            const doc = await generateInvoicePDF(order);
            doc.save(`Invoice_${order.reference || "unknown"}.pdf`);
        } catch (error) {
            console.error("Error generating invoice for download:", error);
            toast.error(error.message || t("ecommerce_order_detail.toasts.invoice_generation_error"));
        } finally {
            setIsGeneratingInvoice(false);
        }
    };

    document.title = t("ecommerce_order_detail.title");

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title={t("ecommerce_order_detail.title")} pageTitle={t("ecommerce_order_detail.page_title")} />
                <ToastContainer closeButton={false} limit={1} />
                <Row>
                    <Col xl={9}>
                        <Card>
                            <CardHeader>
                                <div className="d-flex align-items-center">
                                    <h5 className="card-title flex-grow-1 mb-0">
                                        {t("ecommerce_order_detail.order")} #{order?.reference || "N/A"} ({t(`ecommerce_order_detail.status.${order?.status || "unknown"}`)})
                                    </h5>
                                    <div className="flex-shrink-0">
                                        <Button
                                            color="success"
                                            size="sm"
                                            onClick={printInvoice}
                                            disabled={!order || isGeneratingInvoice}
                                        >
                                            <i className="ri-printer-line align-middle me-1"></i>
                                            {isGeneratingInvoice ? t("ecommerce_order_detail.actions.generating") : t("ecommerce_order_detail.actions.print_invoice")}
                                        </Button>{" "}
                                        <Button
                                            color="primary"
                                            size="sm"
                                            onClick={downloadInvoice}
                                            disabled={!order || isGeneratingInvoice}
                                        >
                                            <i className="ri-download-2-line align-middle me-1"></i>
                                            {isGeneratingInvoice ? t("ecommerce_order_detail.actions.generating") : t("ecommerce_order_detail.actions.download_invoice")}
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {error || !order ? (
                                    <Loader error={error} />
                                ) : (
                                    <Form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            validation.submitForm();
                                        }}
                                    >
                                        <div className="table-responsive table-card">
                                            <table className="table table-nowrap align-middle table-borderless mb-0">
                                                <thead className="table-light text-muted">
                                                <tr>
                                                    <th scope="col">{t("ecommerce_order_detail.table.product_details")}</th>
                                                    <th scope="col">{t("ecommerce_order_detail.table.item_price")}</th>
                                                    <th scope="col">{t("ecommerce_order_detail.table.quantity")}</th>
                                                    <th scope="col">{t("ecommerce_order_detail.table.action")}</th>
                                                    <th scope="col" className="text-end">{t("ecommerce_order_detail.table.total_amount")}</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {validation.values.items.map((item, index) => (
                                                    <EcommerceOrderProduct
                                                        key={index}
                                                        item={item}
                                                        index={index}
                                                        handleQuantityChange={handleQuantityChange}
                                                        handleRemoveProduct={handleRemoveProduct}
                                                    />
                                                ))}
                                                <tr className="border-top border-top-dashed">
                                                    <td colSpan="3"></td>
                                                    <td colSpan="2" className="fw-medium p-0">
                                                        <table className="table table-borderless mb-0">
                                                            <tbody>
                                                            <tr className="border-top border-top-dashed fs-15">
                                                                <th scope="row">{t("ecommerce_order_detail.table.total")}</th>
                                                                <th className="text-end">
                                                                    {validation.values.items
                                                                        .reduce(
                                                                            (sum, item) => sum + item.price * item.quantity,
                                                                            0
                                                                        )
                                                                        .toFixed(2)}{" "}
                                                                    DT
                                                                </th>
                                                            </tr>
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="hstack gap-2 justify-content-end mt-3">
                                            <Button color="success" type="submit" disabled={validation.isSubmitting}>
                                                {validation.isSubmitting ? t("ecommerce_order_detail.actions.updating") : t("ecommerce_order_detail.actions.update_products")}
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl={3}>
                        <Card>
                            <CardHeader>
                                <div className="d-flex">
                                    <h5 className="card-title flex-grow-1 mb-0">{t("ecommerce_order_detail.customer_details")}</h5>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {error || !order ? (
                                    <Loader error={error} />
                                ) : (
                                    <ul className="list-unstyled mb-0 vstack gap-3">
                                        <li>
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                    <h6 className="fs-14 mb-1">
                                                        {order.user?.firstname || ""} {order.user?.lastname || ""}
                                                    </h6>
                                                    <p className="text-muted mb-0">{t("ecommerce_order_detail.customer")}</p>
                                                </div>
                                            </div>
                                        </li>
                                        <li>
                                            <i className="ri-phone-line me-2 align-middle text-muted fs-16"></i>
                                            {order.user?.phoneNumber || "N/A"}
                                        </li>
                                    </ul>
                                )}
                            </CardBody>
                        </Card>
                        <Card>
                            <CardHeader>
                                <div className="d-flex align-items-center">
                                    <h5 className="card-title flex-grow-1 mb-0">{t("ecommerce_order_detail.shipping_address")}</h5>
                                    <Button
                                        color="soft-info"
                                        size="sm"
                                        onClick={() => setIsEditingAddress(!isEditingAddress)}
                                    >
                                        {isEditingAddress ? t("ecommerce_order_detail.actions.cancel") : t("ecommerce_order_detail.actions.edit")}
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {error || !order ? (
                                    <Loader error={error} />
                                ) : isEditingAddress ? (
                                    <Form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            validation.submitForm();
                                        }}
                                    >
                                        <ul className="list-unstyled vstack gap-2 fs-13 mb-0">
                                            <li>
                                                <Input
                                                    name="shippingAddress.fullName"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t("ecommerce_order_detail.form.full_name_placeholder")}
                                                    value={validation.values.shippingAddress.fullName}
                                                    onChange={validation.handleChange}
                                                    invalid={
                                                        validation.touched.shippingAddress?.fullName &&
                                                        validation.errors.shippingAddress?.fullName
                                                    }
                                                />
                                                {validation.touched.shippingAddress?.fullName &&
                                                    validation.errors.shippingAddress?.fullName && (
                                                        <FormFeedback>
                                                            {validation.errors.shippingAddress.fullName}
                                                        </FormFeedback>
                                                    )}
                                            </li>
                                            <li>
                                                <Input
                                                    name="shippingAddress.street"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t("ecommerce_order_detail.form.street_placeholder")}
                                                    value={validation.values.shippingAddress.street}
                                                    onChange={validation.handleChange}
                                                    invalid={
                                                        validation.touched.shippingAddress?.street &&
                                                        validation.errors.shippingAddress?.street
                                                    }
                                                />
                                                {validation.touched.shippingAddress?.street &&
                                                    validation.errors.shippingAddress?.street && (
                                                        <FormFeedback>
                                                            {validation.errors.shippingAddress.street}
                                                        </FormFeedback>
                                                    )}
                                            </li>
                                            <li>
                                                <Input
                                                    name="shippingAddress.city"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t("ecommerce_order_detail.form.city_placeholder")}
                                                    value={validation.values.shippingAddress.city}
                                                    onChange={validation.handleChange}
                                                    invalid={
                                                        validation.touched.shippingAddress?.city &&
                                                        validation.errors.shippingAddress?.city
                                                    }
                                                />
                                                {validation.touched.shippingAddress?.city &&
                                                    validation.errors.shippingAddress?.city && (
                                                        <FormFeedback>
                                                            {validation.errors.shippingAddress.city}
                                                        </FormFeedback>
                                                    )}
                                            </li>
                                            <li>
                                                <Input
                                                    name="shippingAddress.region"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t("ecommerce_order_detail.form.region_placeholder")}
                                                    value={validation.values.shippingAddress.region}
                                                    onChange={validation.handleChange}
                                                    invalid={
                                                        validation.touched.shippingAddress?.region &&
                                                        validation.errors.shippingAddress?.region
                                                    }
                                                />
                                                {validation.touched.shippingAddress?.region &&
                                                    validation.errors.shippingAddress?.region && (
                                                        <FormFeedback>
                                                            {validation.errors.shippingAddress.region}
                                                        </FormFeedback>
                                                    )}
                                            </li>
                                            <li>
                                                <Input
                                                    name="shippingAddress.postalCode"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t("ecommerce_order_detail.form.postal_code_placeholder")}
                                                    value={validation.values.shippingAddress.postalCode}
                                                    onChange={validation.handleChange}
                                                    invalid={
                                                        validation.touched.shippingAddress?.postalCode &&
                                                        validation.errors.shippingAddress?.postalCode
                                                    }
                                                />
                                                {validation.touched.shippingAddress?.postalCode &&
                                                    validation.errors.shippingAddress?.postalCode && (
                                                        <FormFeedback>
                                                            {validation.errors.shippingAddress.postalCode}
                                                        </FormFeedback>
                                                    )}
                                            </li>
                                            <li>
                                                <Input
                                                    name="shippingAddress.phoneNumber"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={t("ecommerce_order_detail.form.phone_number_placeholder")}
                                                    value={validation.values.shippingAddress.phoneNumber}
                                                    onChange={validation.handleChange}
                                                    invalid={
                                                        validation.touched.shippingAddress?.phoneNumber &&
                                                        validation.errors.shippingAddress?.phoneNumber
                                                    }
                                                />
                                                {validation.touched.shippingAddress?.phoneNumber &&
                                                    validation.errors.shippingAddress?.phoneNumber && (
                                                        <FormFeedback>
                                                            {validation.errors.shippingAddress.phoneNumber}
                                                        </FormFeedback>
                                                    )}
                                            </li>
                                            <li>
                                                <Button color="success" type="submit" disabled={validation.isSubmitting}>
                                                    {validation.isSubmitting ? t("ecommerce_order_detail.actions.saving") : t("ecommerce_order_detail.actions.save_address")}
                                                </Button>
                                            </li>
                                        </ul>
                                    </Form>
                                ) : (
                                    <ul className="list-unstyled vstack gap-2 fs-13 mb-0">
                                        <li className="fw-medium fs-14">{order.shippingAddress?.fullName || "N/A"}</li>
                                        <li>{order.shippingAddress?.street || "N/A"}</li>
                                        <li>
                                            {order.shippingAddress?.city || ""}, {order.shippingAddress?.region || ""}{" "}
                                            {order.shippingAddress?.postalCode || ""}
                                        </li>
                                        <li>{order.shippingAddress?.phoneNumber || "N/A"}</li>
                                    </ul>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Modal isOpen={removeModal} toggle={() => setRemoveModal(false)} centered>
                    <ModalHeader toggle={() => setRemoveModal(false)}>
                        {isLastProduct ? t("ecommerce_order_detail.modal.delete_order") : t("ecommerce_order_detail.modal.remove_product")}
                    </ModalHeader>
                    <ModalBody>
                        {isLastProduct
                            ? t("ecommerce_order_detail.modal.delete_order_message", { productName })
                            : t("ecommerce_order_detail.modal.remove_product_message", { productName })}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="light" onClick={() => setRemoveModal(false)}>
                            {t("ecommerce_order_detail.actions.cancel")}
                        </Button>
                        <Button color="danger" onClick={confirmRemove}>
                            {isLastProduct ? t("ecommerce_order_detail.actions.delete_order") : t("ecommerce_order_detail.actions.remove")}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default EcommerceOrderDetail;